import React, { useEffect, useRef, useState } from 'react';

import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { TSelectOptionType } from '../Select.types';
import { IValueContentProps } from './Chips.types';

import InvisibleItems from './InvisibleItems';
import SelectValue from './SelectValue';

import './Chips.scss';

const BUTTON_WIDTH = 50;

const Chips: React.FC<IValueContentProps> = ({
	selectedListOfOptions,
	containerRef,
	removeOption,
	menuPlacement,
	maxMenuChipsHeight,
	menuChipsWidth,
	value,
	readOnly,
}) => {
	const [visibleItems, setVisibleItems] = useState<Array<TSelectOptionType>>(
		value?.slice(0, 1) || [],
	);
	const [invisibleItems, setInvisibleItems] = useState<Array<TSelectOptionType>>(
		value?.slice(1) || [],
	);

	const invisibleItemsRef = useRef<HTMLDivElement>(null);
	const visibleItemsContainerRef = useRef<HTMLDivElement>(null);

	const [isEmptySpaceAvailable, setIsEmptySpaceAvailable] = useState(true);
	const [isOpenMenuInvisibleItems, setIsOpenMenuInvisibleItems] = useState(false);

	useEffect(() => {
		setVisibleItems(value?.slice(0, 1) || []);
		setInvisibleItems(value?.slice(1) || []);
	}, [value]);

	useEffect(() => {
		if (containerRef.current?.offsetWidth && visibleItemsContainerRef.current?.offsetWidth) {
			const visibleItemsContainerWidth = visibleItemsContainerRef.current.offsetWidth;
			const containerWidth = containerRef.current.offsetWidth;
			const emptyWidth = containerWidth - visibleItemsContainerWidth;

			setIsEmptySpaceAvailable(emptyWidth > BUTTON_WIDTH);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visibleItemsContainerRef.current?.offsetWidth, containerRef.current?.offsetWidth, visibleItems]);

	useEffect(() => {
		if (!selectedListOfOptions.length) return;

		if (containerRef.current?.offsetWidth && visibleItemsContainerRef.current?.offsetWidth) {
			const containerWidth = containerRef.current.offsetWidth;
			const visibleItemsContainerWidth = visibleItemsContainerRef.current.offsetWidth;
			const thirdContainerWidth = (containerWidth / 3) * 2;
			const isShouldAddMoreVisibleElements = visibleItemsContainerWidth < thirdContainerWidth;

			if (isShouldAddMoreVisibleElements) {
				const visibleItemsLength = visibleItems.length;
				const offsetFromTheStart = visibleItemsLength + 1;
				const selectedOptionsLength = selectedListOfOptions.length;

				setVisibleItems(selectedListOfOptions.slice(0, offsetFromTheStart));
				setInvisibleItems(selectedListOfOptions.slice(offsetFromTheStart, selectedOptionsLength));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		containerRef?.current?.offsetWidth,
		visibleItemsContainerRef?.current?.offsetWidth,
		selectedListOfOptions,
		visibleItems.length,
	]);

	const toggleMenuInvisibleItems = () => setIsOpenMenuInvisibleItems((prevState) => !prevState);

	useOnClickOutside({
		refs: [invisibleItemsRef],
		handler: () => isOpenMenuInvisibleItems && toggleMenuInvisibleItems(),
	});

	return visibleItems.length ? (
		<div className='f5gChipsValueContentWrapper'>
			<div
				ref={visibleItemsContainerRef}
				className='f5gChipsVisibleItems'
				data-testid='visible-items-wrapper'
			>
				{
					visibleItems.map((option, index) => {
							const isOverflow = (visibleItems.length === 1 && !isEmptySpaceAvailable) ||
								(
									visibleItems.length - 1 === index &&
									visibleItems.length > 1 &&
									!isEmptySpaceAvailable
								) || (visibleItems.length === 1 && selectedListOfOptions.length > 1);

							return (
								<SelectValue
									key={option?.label + index.toString()}
									isOverflow={isOverflow}
									option={option}
									readOnly={readOnly}
									removeHandler={removeOption}
								/>
							);
						},
					)}
			</div>
			{!!invisibleItems.length && (
				<div
					ref={invisibleItemsRef}
					className='f5gChipsInvisibleItems'
				>
					<div
						className='f5gChipsInvisibleItemsCount'
						onClick={toggleMenuInvisibleItems}
					>
						+{invisibleItems.length}
					</div>
					{isOpenMenuInvisibleItems && (
						<InvisibleItems
							containerRef={containerRef}
							invisibleItems={invisibleItems}
							maxMenuChipsHeight={maxMenuChipsHeight}
							menuChipsWidth={menuChipsWidth}
							menuPlacement={menuPlacement}
							readOnly={readOnly}
							removeHandler={removeOption}
						/>
					)}
				</div>
			)}
		</div>
	) : null;
};

export default Chips;
